import './polyfill';
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import App, { IAppProps } from './App';
import { IVehicleInfo } from './types/IVehicleInfo';
import { IAppConfig } from './types/IAppConfig';
import { vinDecode, plateDecode, IDecodeResp } from './api/decoder';
import { IStartQuote } from './types/IStartQuote';
import { checkHost } from './utils/localStorage';
import { IEligibleOffer, IIneligibleOffer, resetSavedResult, saveResultToLocalStorage } from '@kmx/io-core/storage';
import { isValidVin } from './utils/validation';
import { IVinMatchingStyles } from './types/IVinMatchingStyles';
import { initializeAppConfig } from './utils/appConfig';
import { IClientInfoContext } from './context/clientInfo';

interface IcoConfiguration {
    appConfig?: IAppConfig;
    authRefreshHandler: () => Promise<string>;
    bypassRefreshOfferPrompt?: boolean;
    clientFeatures?: string[];
    clientFeaturesEventId?: string;
    clientInfo?: IClientInfoContext;
    customerEmail?: string;
    containerId: string;
    enabledFeatures?: string[];
    extraQuestions?: string[];
    handleIneligible?: boolean;
    offerId?: string;
    originPage?: string;
    renderPlateVinForm?: boolean;
    saveResult?: boolean;
    startingMethod?: string;
    storeId?: string;
    vehicleInformation: IVehicleInfo;
}

export default class InstantCashOffers {
    // track quoteIds by VIN from external calls to getEligibility
    private static vinToQuoteIdCache = new Map<string, IStartQuote>();
    private static vehicleDecodeCache = new Map<string, IDecodeResp>();
    private static vehicleStylesCache = new Map<string, IVinMatchingStyles>();
    private static root: ReactDOM.Root = null;
    private static app: React.ReactElement = null;
    private static config: IcoConfiguration = null;

    // Mounts and also updates ICO
    static initialize(config: IcoConfiguration): void {
        //clear the token to prevent reuse on a subsequent intialize (value microsite)
        process.env['TOKEN'] = null;
        process.env['AUTH_REFRESH_HANDLER'] = config.authRefreshHandler;
        initializeAppConfig(config.appConfig);

        // Checkout IO v2 fix, ignore previously decoded vehicle profile
        delete config.vehicleInformation?.profile;

        this.config = config;
        this.root = ReactDOM.createRoot(document.getElementById(config.containerId));
        this.app = <App {...this.getMapConfigToProps(this.config)} />;

        this.root.render(this.app);
    }

    // Unmounts ICO
    // TODO: I don't understand why they the containerId is needed to be passed in? Shouldn't that already been know.
    // Need to check to see if anyone is using this method.
    static cancel(containerId: string): void {
        const elem = document.getElementById(containerId);
        if (this.root && elem) {
            this.root.unmount();
        }
    }

    // Hides ICO, preserves state
    static hide(config: IcoConfiguration): void {
        const elem = document.getElementById(config.containerId);
        if (!elem) {
            this.root = ReactDOM.createRoot(document.getElementById(config.containerId));
        }

        this.root.render(
            <App
                vehicleInfo={config.vehicleInformation}
                authRefresh={config.authRefreshHandler}
                hidden={true}
                handleIneligible={config.handleIneligible ? true : false}
                saveResult={checkHost() && config.saveResult === false ? false : true} // default to true
                enabledFeatures={config.enabledFeatures || []}
                extraQuestions={config.extraQuestions || null}
                clientFeatures={config.clientFeatures || []}
            />
        );
    }

    static decodeVin(vin: string, authRefreshHandler: () => Promise<string>): Promise<IDecodeResp> {
        if (authRefreshHandler) process.env['AUTH_REFRESH_HANDLER'] = authRefreshHandler;
        return new Promise((res, rej) => {
            vinDecode(vin)
                .then(response => {
                    this.vehicleDecodeCache.set(vin, response);
                    res(response);
                })
                .catch(err => rej(err));
        });
    }

    static decodePlate(
        licensePlate: string,
        state: string,
        authRefreshHandler: () => Promise<string>
    ): Promise<IDecodeResp> {
        if (authRefreshHandler) process.env['AUTH_REFRESH_HANDLER'] = authRefreshHandler;
        return new Promise((res, rej) => {
            plateDecode(licensePlate, state)
                .then(response => {
                    if (response && response.vin) {
                        this.vehicleDecodeCache.set(response.vin, response);
                    }
                    res(response);
                })
                .catch(err => rej(err));
        });
    }

    static getEligibility(): Promise<boolean> {
        return Promise.resolve(true);
    }

    static initializeFeatureTests(authRefreshHandler: () => Promise<string>): void {
        process.env['AUTH_REFRESH_HANDLER'] = authRefreshHandler;
    }

    static saveResultToLocalStorage(type: string, data: IEligibleOffer | IIneligibleOffer): void {
        saveResultToLocalStorage(type, data);
    }

    static getSavedResultFromLocalStorage(type: string): IEligibleOffer | IIneligibleOffer {
        return getResultFromLocalStorage(type);
    }

    static resetSavedIcoResult(): void {
        resetSavedResult();
    }

    static validateVin(vin: string): boolean {
        return isValidVin(vin);
    }

    private static getMapConfigToProps(config: IcoConfiguration, hidden = false): IAppProps {
        const vin = config.vehicleInformation?.vin;
        return {
            authRefresh: config.authRefreshHandler,
            bypassRefreshOfferPrompt: config.bypassRefreshOfferPrompt,
            clientFeatures: config.clientFeatures || [],
            clientFeaturesEventId: config.clientFeaturesEventId || '',
            clientInfo: config.clientInfo,
            customerEmail: config.customerEmail || '',
            enabledFeatures: config.enabledFeatures || [],
            extraQuestions: config.extraQuestions || null,
            handleIneligible: config.handleIneligible ? true : false,
            hidden,
            offerId: config.offerId,
            originPage: config.originPage,
            renderPlateVinForm: config.renderPlateVinForm,
            saveResult: checkHost() && config.saveResult === false ? false : true, // default to true
            startingMethod: config.startingMethod,
            startQuote: vin == null ? null : { [vin]: this.vinToQuoteIdCache.get(vin) },
            storeId: config.storeId,
            vehicleInfo: config.vehicleInformation,
            vehicleStyles: vin == null ? null : { [vin]: this.vehicleStylesCache.get(vin) },
        };
    }
}
